// Serviço da central de pendências (domínio tasks).
// Regras: pendência nunca some; cancelar exige motivo; toda mudança
// relevante gera comentário de sistema + notificação + timeline (se houver paciente).
import { createClient } from '@/lib/supabase/client'
import type {
  CategoriaTarefa, PrioridadeTarefa, StatusTarefa, Tarefa,
  TarefaComentario, TarefaDelegacao,
} from '@/lib/types'
import { STATUS_TAREFA_ABERTOS, STATUS_TAREFA_LABEL } from '@/lib/types'
import { hojeISO } from '@/lib/datas'
import { registrarAuditoria } from '@/services/identity'
import { notificar } from '@/services/notificacoes'
import { registrarEvento } from '@/services/timeline'

export interface TarefaComRelacoes extends Tarefa {
  paciente_nome: string | null
}

const CAMPOS = '*, pacientes(nome)'

function mapRow(row: Record<string, unknown>): TarefaComRelacoes {
  const raw = row.pacientes
  const pac = (Array.isArray(raw) ? raw[0] : raw) as { nome: string } | null
  const { pacientes: _drop, ...resto } = row
  void _drop
  return { ...(resto as unknown as Tarefa), paciente_nome: pac?.nome ?? null }
}

export interface FiltrosTarefas {
  aba?: 'abertas' | 'concluidas' | 'todas'
  responsavelUserId?: string | 'todos'
  prioridade?: PrioridadeTarefa | 'todas'
  categoria?: CategoriaTarefa | 'todas'
  pacienteId?: string
  /** só as que vencem até hoje (inclui atrasadas) */
  vencendoHoje?: boolean
}

export async function listTarefas(workspaceId: string, f: FiltrosTarefas = {}): Promise<TarefaComRelacoes[]> {
  const supabase = createClient()
  let q = supabase
    .from('tarefas')
    .select(CAMPOS)
    .eq('workspace_id', workspaceId)
    .order('prazo', { ascending: true, nullsFirst: false })
    .order('created_at', { ascending: false })
    .limit(500)

  if (f.aba === 'abertas' || f.aba === undefined) q = q.in('status', STATUS_TAREFA_ABERTOS)
  else if (f.aba === 'concluidas') q = q.in('status', ['concluida', 'cancelada'])
  if (f.responsavelUserId && f.responsavelUserId !== 'todos') q = q.eq('responsavel_user_id', f.responsavelUserId)
  if (f.prioridade && f.prioridade !== 'todas') q = q.eq('prioridade', f.prioridade)
  if (f.categoria && f.categoria !== 'todas') q = q.eq('categoria', f.categoria)
  if (f.pacienteId) q = q.eq('paciente_id', f.pacienteId)
  if (f.vencendoHoje) q = q.lte('prazo', hojeISO())

  const { data, error } = await q
  if (error) throw error
  return (data ?? []).map(r => mapRow(r as Record<string, unknown>))
}

export async function getTarefa(workspaceId: string, id: string): Promise<{
  tarefa: TarefaComRelacoes
  comentarios: TarefaComentario[]
  delegacoes: TarefaDelegacao[]
} | null> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('tarefas')
    .select(CAMPOS)
    .eq('workspace_id', workspaceId)
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  if (!data) return null
  const [com, del] = await Promise.all([
    supabase.from('tarefa_comentarios').select('*').eq('tarefa_id', id).order('created_at'),
    supabase.from('tarefa_delegacoes').select('*').eq('tarefa_id', id).order('created_at'),
  ])
  return {
    tarefa: mapRow(data as Record<string, unknown>),
    comentarios: (com.data ?? []) as TarefaComentario[],
    delegacoes: (del.data ?? []) as TarefaDelegacao[],
  }
}

export interface DadosTarefa {
  titulo: string
  descricao?: string | null
  paciente_id?: string | null
  consulta_id?: string | null
  categoria: CategoriaTarefa
  responsavel_user_id?: string | null
  prioridade: PrioridadeTarefa
  prazo?: string | null
  visibilidade?: 'equipe' | 'medicos'
  etiquetas?: string[]
}

export async function criarTarefa(params: {
  workspaceId: string
  dados: DadosTarefa
  criadorUserId: string
}): Promise<string> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('tarefas')
    .insert({ workspace_id: params.workspaceId, ...params.dados })
    .select('id')
    .single()
  if (error) throw error
  const id = (data as { id: string }).id

  if (params.dados.responsavel_user_id && params.dados.responsavel_user_id !== params.criadorUserId) {
    await notificar({
      workspaceId: params.workspaceId,
      userId: params.dados.responsavel_user_id,
      tipo: 'tarefa_atribuida',
      titulo: `Nova pendência: ${params.dados.titulo}`,
      corpo: params.dados.prazo ? `Prazo: ${params.dados.prazo.split('-').reverse().join('/')}` : null,
      link: `/pendencias/nova?id=${id}`,
    })
  }
  if (params.dados.paciente_id) {
    await registrarEvento({
      workspaceId: params.workspaceId,
      pacienteId: params.dados.paciente_id,
      tipo: 'tarefa_criada',
      titulo: `Pendência criada: ${params.dados.titulo}`,
      eventoDominio: 'task.created',
    })
  } else {
    // outbox mesmo sem paciente
    try {
      await supabase.from('domain_events').insert({
        workspace_id: params.workspaceId,
        tipo: 'task.created',
        entidade: 'tarefas',
        registro_id: id,
      })
    } catch { /* silencioso */ }
  }
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'tarefa_criada',
    entidade: 'tarefas',
    registroId: id,
  })
  return id
}

export async function atualizarTarefa(params: {
  workspaceId: string
  id: string
  dados: Partial<DadosTarefa>
}): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase
    .from('tarefas')
    .update(params.dados)
    .eq('id', params.id)
    .eq('workspace_id', params.workspaceId)
  if (error) throw error
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'tarefa_atualizada',
    entidade: 'tarefas',
    registroId: params.id,
    dados: params.dados as Record<string, unknown>,
  })
}

async function comentarioSistema(workspaceId: string, tarefaId: string, texto: string) {
  try {
    const supabase = createClient()
    await supabase.from('tarefa_comentarios').insert({
      workspace_id: workspaceId,
      tarefa_id: tarefaId,
      texto,
      sistema: true,
    })
  } catch { /* silencioso */ }
}

export async function mudarStatusTarefa(params: {
  workspaceId: string
  tarefa: Tarefa
  novoStatus: StatusTarefa
  motivoCancelamento?: string
  notificarUserId?: string | null
}): Promise<void> {
  if (params.novoStatus === 'cancelada' && !params.motivoCancelamento?.trim()) {
    throw new Error('Cancelamento exige motivo.')
  }
  const supabase = createClient()
  const { error } = await supabase
    .from('tarefas')
    .update({
      status: params.novoStatus,
      motivo_cancelamento: params.novoStatus === 'cancelada' ? params.motivoCancelamento!.trim() : params.tarefa.motivo_cancelamento,
    })
    .eq('id', params.tarefa.id)
    .eq('workspace_id', params.workspaceId)
  if (error) throw error

  await comentarioSistema(
    params.workspaceId,
    params.tarefa.id,
    params.novoStatus === 'cancelada'
      ? `Status: ${STATUS_TAREFA_LABEL[params.tarefa.status]} → Cancelada. Motivo: ${params.motivoCancelamento}`
      : `Status: ${STATUS_TAREFA_LABEL[params.tarefa.status]} → ${STATUS_TAREFA_LABEL[params.novoStatus]}`
  )

  if (params.notificarUserId) {
    await notificar({
      workspaceId: params.workspaceId,
      userId: params.notificarUserId,
      tipo: 'tarefa_status',
      titulo: `Pendência ${STATUS_TAREFA_LABEL[params.novoStatus].toLowerCase()}: ${params.tarefa.titulo}`,
      link: `/pendencias/nova?id=${params.tarefa.id}`,
    })
  }
  if (params.tarefa.paciente_id) {
    await registrarEvento({
      workspaceId: params.workspaceId,
      pacienteId: params.tarefa.paciente_id,
      tipo: 'tarefa_status',
      titulo: `Pendência ${STATUS_TAREFA_LABEL[params.novoStatus].toLowerCase()}: ${params.tarefa.titulo}`,
      eventoDominio: params.novoStatus === 'concluida' ? 'task.completed' : 'task.updated',
    })
  }
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: `tarefa_${params.novoStatus}`,
    entidade: 'tarefas',
    registroId: params.tarefa.id,
    dados: params.motivoCancelamento ? { motivo: params.motivoCancelamento } : {},
  })
}

export async function delegarTarefa(params: {
  workspaceId: string
  tarefa: Tarefa
  paraUserId: string
  paraNome: string
  paraEhMedico: boolean
  instrucao?: string | null
  prazo?: string | null
}): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase.from('tarefa_delegacoes').insert({
    workspace_id: params.workspaceId,
    tarefa_id: params.tarefa.id,
    para_user_id: params.paraUserId,
    instrucao: params.instrucao ?? null,
    prazo: params.prazo ?? null,
  })
  if (error) throw error

  const { error: e2 } = await supabase
    .from('tarefas')
    .update({
      responsavel_user_id: params.paraUserId,
      status: params.paraEhMedico ? 'aguardando_medico' : 'aguardando_secretaria',
      prazo: params.prazo ?? params.tarefa.prazo,
    })
    .eq('id', params.tarefa.id)
    .eq('workspace_id', params.workspaceId)
  if (e2) throw e2

  await comentarioSistema(
    params.workspaceId,
    params.tarefa.id,
    `Delegada para ${params.paraNome}${params.instrucao ? ` — "${params.instrucao}"` : ''}${params.prazo ? ` (prazo ${params.prazo.split('-').reverse().join('/')})` : ''}`
  )
  await notificar({
    workspaceId: params.workspaceId,
    userId: params.paraUserId,
    tipo: 'tarefa_delegada',
    titulo: `Pendência delegada a você: ${params.tarefa.titulo}`,
    corpo: params.instrucao ?? null,
    link: `/pendencias/nova?id=${params.tarefa.id}`,
  })
  if (params.tarefa.paciente_id) {
    await registrarEvento({
      workspaceId: params.workspaceId,
      pacienteId: params.tarefa.paciente_id,
      tipo: 'tarefa_delegada',
      titulo: `Pendência delegada: ${params.tarefa.titulo}`,
      eventoDominio: 'task.assigned',
    })
  }
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'tarefa_delegada',
    entidade: 'tarefas',
    registroId: params.tarefa.id,
    dados: { para: params.paraUserId },
  })
}

export async function comentarTarefa(params: {
  workspaceId: string
  tarefaId: string
  texto: string
}): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase.from('tarefa_comentarios').insert({
    workspace_id: params.workspaceId,
    tarefa_id: params.tarefaId,
    texto: params.texto.trim(),
  })
  if (error) throw error
}
